/**
 * SessionPlannerLayout — two-pane desktop layout primitive for
 * the Session Planner (issue #437).
 *
 * At >=1024px the planner renders as a `grid-template-columns:
 * 1fr 1.4fr; gap: 32px` two-column grid. The left pane scrolls
 * independently of the right pane and pins its content via
 * `position: sticky; top: 24px` so focus goals + the activity
 * library stay visible while the SLP edits the plan on the right.
 *
 * At <1024px the layout collapses to a single-column vertical
 * stack (the historical mobile / tablet behavior).
 *
 * The pane contents are supplied as `leftPane` + `rightPane`
 * slots; the layout component owns the responsive scaffolding
 * only — it doesn't know about focus goals, activity libraries,
 * or plan editors. That keeps it testable + reusable.
 *
 * The "sticky generate button" behavior is supplied by the
 * caller via the `rightPaneSticky` slot. The sticky slot is
 * rendered at the top of the right pane with a frosted-glass
 * background so the SLP can re-generate without scrolling back
 * up. The right pane scrolls under it.
 */
import type { ReactElement, ReactNode } from 'react';
import { useBreakpoint } from '../lib/ui/useBreakpoint';

export interface SessionPlannerLayoutProps {
  /** The left pane contents (focus goals + activity library). */
  leftPane: ReactNode;
  /**
   * The right pane contents (plan editor + share buttons).
   * Scrolled under the sticky right-pane slot.
   */
  rightPane: ReactNode;
  /**
   * The sticky element pinned at the top of the right pane
   * (generate button + free-tier counter). Renders inside the
   * right pane so it scrolls with the pane but pins at the top
   * via `position: sticky; top: 0`.
   */
  rightPaneSticky?: ReactNode;
  /** Optional className appended to the root element. */
  className?: string;
}

export function SessionPlannerLayout({
  leftPane,
  rightPane,
  rightPaneSticky,
  className,
}: SessionPlannerLayoutProps): ReactElement {
  const bp = useBreakpoint();
  const isDesktop = bp === 'desktop' || bp === 'wide';

  if (!isDesktop) {
    // Mobile / tablet: single column.
    return (
      <div className={className} data-testid="planner-layout" data-layout="stacked">
        {rightPaneSticky}
        {leftPane}
        {rightPane}
      </div>
    );
  }

  return (
    <div
      className={`planner-grid ${className ?? ''}`.trim()}
      data-testid="planner-layout"
      data-layout="two-pane"
    >
      <div
        className="planner-grid-col planner-grid-col--left"
        data-testid="planner-layout-left"
      >
        {leftPane}
      </div>
      <div
        className="planner-grid-col planner-grid-col--right"
        data-testid="planner-layout-right"
      >
        {rightPaneSticky && (
          <div
            className="planner-grid-sticky"
            data-testid="planner-layout-right-sticky"
          >
            {rightPaneSticky}
          </div>
        )}
        {rightPane}
      </div>
    </div>
  );
}
